"use client";

/**
 * **El `<script>` inline de arranque, envuelto para que React no proteste.**
 *
 * React avisa en desarrollo cada vez que **crea** un `<script>` del lado del cliente —*"Encountered a
 * script tag while rendering React component"*— y además lo reemplaza por un `<div>` vacío. La condición
 * exacta está en `isScriptDataBlock()` de `react-dom-client.development.js`, y mira **solo el `type`**:
 * avisa cuando falta, cuando es `module`/`importmap`/`speculationrules`, o cuando es uno de los 13 MIME
 * ejecutables (`text/javascript`, `application/javascript`, …). Con cualquier otro —`text/plain`
 * incluido— se calla. Por eso los `<script type="application/ld+json">` de este repo **no hace falta
 * envolverlos**: ese `type` ya no dispara el aviso.
 *
 * De ahí el patrón que documenta Next (`preventing-flash-before-hydration.md`, *"Extracting a reusable
 * component"*): `text/javascript` en el servidor y `text/plain` en el cliente.
 *
 * 🔑 **Y por eso este archivo lleva `"use client"`.** El ternario solo sirve si **se vuelve a evaluar en el
 * navegador**. Escrito dentro de un Server Component, `typeof window === "undefined"` se resuelve una sola
 * vez —en el servidor— y el payload RSC viaja siempre con `text/javascript`: el warning sale igual. Es el
 * error que tenían los scripts de arranque de todo el ecosistema hasta el 2026-09-09.
 *
 * ⚠️ **El script se sigue ejecutando igual.** En el HTML del servidor sale como `text/javascript` y el
 * navegador lo corre **mientras parsea el `<head>`**, antes de que React exista — que es lo único que
 * importa para no parpadear. Al hidratar, el cliente produce `text/plain` y `suppressHydrationWarning` hace
 * que React acepte lo que ya está en el DOM.
 *
 * No recibe datos de usuario: el `html` lo arma siempre el componente de servidor que lo usa.
 */
export function InlineScript({ html }: { html: string }) {
  return (
    <script
      type={typeof window === "undefined" ? "text/javascript" : "text/plain"}
      suppressHydrationWarning
      // biome-ignore lint/security/noDangerouslySetInnerHtml: es el punto del componente — script de arranque pre-hidratación
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
